import React, { useCallback, useMemo, useState } from 'react';
import { Typography } from 'fronton-react';
import { InfoIcon } from '@fronton/icons-react';
import { ButtonWithGAClickButton, Section, SwiperClass } from 'common/components';
import { ReviewsCatalogModalB, ReviewStructure } from 'common/components/reviews-section';
import { analyticsUtils } from 'utils/analytics-utils';
import { createElementKey } from 'utils';
import { ReviewHint } from './review-hint';
import {
  reviewCard,
  reviewInfoIcon,
  reviewList,
  reviewPhoto,
  reviewsShowMoreBtnContainer,
  reviewsTitle,
} from './reviews-styles';

export const REVIEWS_PER_PAGE = 5;

type TProps = {
  elementBlock: string;
  reviews: ReviewStructure[];
  title?: string;
  reviewModalButton: React.ReactNode;
};

export const Reviews: React.FC<TProps> = ({ elementBlock, reviews, title, reviewModalButton }) => {
  const [reviewsPerPage, setReviewsPerPage] = useState<number>(REVIEWS_PER_PAGE);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeReview, setActiveReview] = useState<ReviewStructure | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

  const shownReviewsList = useMemo(() => reviews.slice(0, reviewsPerPage), [
    reviewsPerPage, reviews,
  ]);

  const isShowMoreBtnVisible = useMemo(() => reviewsPerPage < reviews.length, [
    reviewsPerPage, reviews,
  ]);

  const handleClickMoreBtn = () => {
    setReviewsPerPage(reviewsPerPage + REVIEWS_PER_PAGE);
  };

  const handleReviewClick = useCallback((review: ReviewStructure) => () => {
    setActiveReview(review);
    setIsModalOpen(true);
    analyticsUtils.clickButtonEvent(`${elementBlock} / Reviews`, 'Отзыв', 'Отзывы клиентов');
  }, [elementBlock]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setThumbsSwiper(null);
  };

  return (
    <Section>
      {title && (
        <Typography className={reviewsTitle} variant="h2" size="extra">
          {title}
        </Typography>
      )}

      <ul className={reviewList}>
        {shownReviewsList.map((review, index) => (
          <li key={createElementKey('review', index)}>
            <button className={reviewCard} onClick={handleReviewClick(review)}>
              <img
                alt="Фото для отзыва"
                className={reviewPhoto}
                src={review.images[0]}
              />

              <ReviewHint
                rating={review.rating}
                text={review.text}
                store={review.store}
                date={review.date}
              />

              <div className={reviewInfoIcon}>
                <InfoIcon type='outline' size='m' color='text-invert' />
              </div>
            </button>
          </li>
        ))}
      </ul>
      {isShowMoreBtnVisible && (
        <div className={reviewsShowMoreBtnContainer}>
          <ButtonWithGAClickButton
            elementBlock={`${elementBlock} / Reviews`}
            elementName="Отзывы клиентов"
            elementText="Показать еще"
            onClick={handleClickMoreBtn}
            variant="secondary"
            rounded
          >
            Показать еще
          </ButtonWithGAClickButton>
        </div>
      )}

      {activeReview && (
        <ReviewsCatalogModalB
          activeReview={activeReview}
          isOpen={isModalOpen}
          modalButton={reviewModalButton}
          onClose={handleModalClose}
          setThumbsSwiper={setThumbsSwiper}
          thumbsSwiper={thumbsSwiper}
        />
      )}
    </Section>
  );
}